import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { ButtonLink, Eyebrow, GhostLink, Reveal } from "../components/ui-bits";
import { CTABand, PageHero, SectionHeading } from "../components/PageSections";

const VALUES = [
  {
    title: "A real brick-and-mortar shop",
    text: "Not a national order website, not a call center. When you call, the phone rings at 1117 Main Street, and the person who answers knows the flowers in the cooler.",
  },
  {
    title: "Fresher, by design",
    text: "Our flowers come from our own walk-in cooler and local growers — so they arrive at their peak and last longer than chain or supermarket flowers.",
  },
  {
    title: "Any size, any budget",
    text: "From a single stem to fifty centerpieces. We'll always tell you honestly what your budget can do beautifully.",
  },
  {
    title: "Delivered by our own hands",
    text: "Our shop van and professional driver hand-deliver every order across the Kent County area — and confirm every single one.",
  },
];

const STORY = [
  "Our family's love of flowers began in 1895, when my grandparents established Quidnick Green Houses in Coventry, Rhode Island. Those greenhouses became the foundation of a family tradition built on hard work, craftsmanship, and a passion for flowers.",
  "My father, Jack Iannotti, was the oldest of five brothers. Growing up in the greenhouse business, he learned the value of hard work and developed a love for flowers that would shape his future.",
  "In 1974, my father and my mother, Millie Iannotti, set out to build something of their own. Together, they opened JIF – Jack Iannotti Flowers on Main Street in Coventry.",
  "Their vision was simple: beautiful flowers, quality craftsmanship, personal service, and treating every customer like family.",
  "I'm Edward Iannotti, the middle of Jack and Millie's three sons, and today I proudly carry on the business my parents started more than 50 years ago.",
  "I grew up surrounded by flowers, watching my parents build lasting relationships with customers and their families. Those relationships have always meant just as much to us as the flowers themselves.",
  "Over the decades, we've had the privilege of being part of generations of family memories. From prom corsages and wedding bouquets to anniversaries, new babies, and heartfelt sympathy tributes, our flowers have helped people express what words sometimes cannot.",
  "Coventry has changed tremendously since my grandparents opened their greenhouses in 1895. But the values behind our family business have remained the same.",
  "We still believe in hand-designed arrangements, personal attention, quality flowers, and knowing the people we serve.",
  "For our family, flowers have never been just a business. They're our history, our livelihood, and our connection to the community we've called home for generations.",
  "From my grandparents' greenhouses in 1895, to the flower shop my parents built in 1974, to the shop I proudly run today — our roots remain right here in Coventry.",
];

export default function About() {
  return (
    <>
      <Seo
        title="Our Story | JIF Jack Iannotti Flowers | Coventry RI Florist"
        description="Discover the Iannotti family's floral roots dating to 1895. Founded by Jack and Millie Iannotti in 1974, JIF Flowers proudly serves Coventry, RI."
      />
      <PageHero
        eyebrow="Our Story"
        lines={["Our Family's Floral Legacy"]}
        lede="Rooted in Coventry since 1895 — from my grandparents' greenhouses to the flower shop my parents built on Main Street in 1974."
        image="https://images.pexels.com/photos/5414032/pexels-photo-5414032.jpeg?auto=compress&cs=tinysrgb&w=1200"
        imageAlt="Fresh flowers inside the JIF Flowers shop on Main Street, Coventry"
        secondary={{ href: "/gallery", label: "See Our Work", testid: "hero-gallery-link" }}
      />

      <section className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-24" data-testid="family-story">
        <Reveal>
          <Eyebrow>Family Floral Heritage Since 1895</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl">
            Rooted in Coventry Since 1895
          </h2>
          <div className="gold-rule-left mt-5" aria-hidden="true" />
          {STORY.map((p, i) => (
            <p key={i} className="mt-6 text-[17px] leading-relaxed text-charcoal/85">
              {p}
            </p>
          ))}
          <div className="gold-rule mt-10" aria-hidden="true" />
          <p className="mt-8 font-serif text-2xl italic text-plum sm:text-3xl">Edward Iannotti</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-eyebrow text-sage">
            JIF – Jack Iannotti Flowers
          </p>
          <div className="mt-10">
            <ButtonLink href="/occasions" color="plum" testid="shop-our-flowers-link">
              Shop Our Flowers <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </ButtonLink>
          </div>
        </Reveal>
      </section>

      <section className="bg-sagetint">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <SectionHeading eyebrow="What Makes Us Different" title="Why Coventry keeps calling" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-xl bg-ivory p-8 shadow-sm">
                  <h3 className="font-serif text-2xl font-medium text-plum">{v.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-charcoal/80">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-20 text-center sm:px-8">
        <Reveal>
          <h2 className="font-serif text-3xl font-medium text-plum sm:text-4xl">Come say hello</h2>
          <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-charcoal/85">
            The walk-in cooler is always stocked with ready-to-go arrangements, roses, and mixed bouquets. Stop in,
            browse, and let us wrap something up — or call ahead and we'll have it waiting.
          </p>
          <div className="mt-7">
            <GhostLink href="/contact" testid="about-contact-link">
              Hours & Directions
            </GhostLink>
          </div>
        </Reveal>
      </section>

      <CTABand title="Three generations of Coventry's occasions — we'd be honored with yours" />
    </>
  );
}
